import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { modal } from "commonAssets/lib/modal";
import { clearMessage } from "commonAssets/lib/clearMessage";
import {
    outputDiv,
    appConfigDev,
    appConfigProd,
    appConfigKey,
    reCAPTCHAv3,
    i18n,
} from "commonAssets/js/constants";
import {
    FormFields,
    FormFieldsLabel,
} from "commonAssets/components/FormBase/FormFields";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { resolveHtmlFields } from "commonAssets/lib/resolveHtmlFields";

export class LookupTool {
    /**
     * @param {Object} options
     * @param {string} options.configPath
     * @param {string} options.userLanguage
     * @param {ErrorHandler} options.errorHandler
     */
    constructor({ configPath, userLanguage, errorHandler }) {
        this.configPath = configPath;
        this.userLanguage = userLanguage;
        this.errorHandler = errorHandler;
        this.domain = getHostname();
        this.apiClient = createApiClient();
    }

    /**
     * Create lookup form
     * @param {Object} params
     * @param {Function} params.onLookupSuccess
     * @param {Function} params.onLookupError
     * @returns {Promise<HTMLFormElement>}
     */
    async createForm({ onLookupSuccess, onLookupError }) {
        try {
            const form = createElement("form", {
                class: "needs-validation",
                id: "lookup-form",
                novalidate: true,
            });

            const fieldset = createElement("fieldset", {
                class: "p-3 border rounded",
                id: "lookup-tool",
            });

            const submitBtn = createElement(
                "div",
                undefined,
                [],
                `
                <input 
                    type="submit" 
                    class="btn btn-primary" id="submit-lookup" 
                    value="${i18n.getTranslation("submit_btn")}" 
                    aria-label="${i18n.getTranslation("submit_btn")}"
                >
            `
            );

            const config = await getConfig(this.configPath, this.userLanguage);
            const formFields = config.fields.map(createFormField);

            fieldset.appendChild(createElement("p", {}, [], config.body));
            fieldset.append(...formFields);
            fieldset.appendChild(submitBtn);

            const lookupResults = createElement("div", {
                id: "lookup_result",
            });

            form.addEventListener(
                "submit",
                (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (form.checkValidity()) {
                        handleSubmit({
                            event,
                            lookupResults,
                            config,
                            errorHandler: this.errorHandler,
                            onSuccess: onLookupSuccess,
                            onError: onLookupError,
                        });
                    }

                    form.classList.add("was-validated");
                },
                false
            );

            fieldset.appendChild(lookupResults);
            form.appendChild(fieldset);
            return form;
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Could not create lookup form", { cause: error })
            );
            this.errorHandler.display(error);
        }
    }

    /**
     * Render lookup page
     * @param {Object} params
     * @param {Function} params.onLookupSuccess
     * @param {Function} params.onLookupError
     * @returns {void}
     */
    async renderPage({
        onLookupSuccess,
        onLookupError,
        skipDeadlineCheck = false,
    }) {
        try {
            if (!skipDeadlineCheck) {
                const result = await this.apiClient.claimsDeadlineGet({
                    domain: this.domain,
                });

                if (result.data.error) {
                    outputDiv.appendChild(
                        createElement(
                            "div",
                            {
                                class: "text-center text-danger fw-bold fs-4 py-4",
                                "aria-label": result.data.error,
                            },
                            [],
                            `${result.data.error} <br> ${i18n.getTranslation("return_to_home")}`
                        )
                    );
                    return;
                }
            }

            const form = await this.createForm({
                onLookupSuccess,
                onLookupError,
            });
            const container = createElement(
                "div",
                {
                    class: "display-form col-md-6 offset-md-3 bg-light text-dark mb-5",
                },
                [form]
            );

            outputDiv.appendChild(container);
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Could not render lookup page", { cause: error })
            );
            this.errorHandler.display(error);
        } finally {
            loadingGif(loading, "hide", 250);
        }
    }
}

/**
 * Create form field
 * @param {Object} field
 * @returns {HTMLElement}
 */
function createFormField(field) {
    const formFieldGroup = createElement("div", {
        class: "mb-3 form-group " + field.parentClass,
    });
    new FormFieldsLabel(field, field.label, formFieldGroup);
    const formField = new FormFields(
        field,
        field.className,
        field.name,
        formFieldGroup,
        field.required
    );

    // FormFields doesn't handle email and tel properly so we need to set it manually
    if (field.subtype) {
        formField.setAttribute("type", field.subtype);
    }

    formFieldGroup.appendChild(formField);
    return formFieldGroup;
}

/**
 * Handle form submission
 * @param {Object} params
 * @param {Event} params.event
 * @param {HTMLElement} params.lookupResults
 * @param {Object} params.config
 * @param {ErrorHandler} params.errorHandler
 * @param {Function?} params.onSuccess
 * @param {Function?} params.onError
 * @returns {void}
 */
function handleSubmit({
    event,
    lookupResults,
    config,
    errorHandler,
    onSuccess,
    onError = renderErrorMessage,
}) {
    const { elements } = event.target;
    lookupResults.innerHTML = "";

    const searchFields = config.fields.reduce((acc, field) => {
        acc[field.name] = elements[field.name].value.trim();
        return acc;
    }, {});

    const lookup = {
        domain: getHostname(),
        searchIndex: 0,
        searchFields,
    };

    clearMessage();

    // Display loading gif
    loadingGif(loading, "show");

    const api = createApiClient();

    // Retrieve class member data (use POST to pass grecaptchanotice id and confirmation code privately)
    grecaptcha.enterprise
        .execute(reCAPTCHAv3, { action: "submit" })
        .then((token) => {
            api.classMemberSearchV2Post({}, { ...lookup, token })
                .then((result) => {
                    if (result.data.error) {
                        onError(result.data.error);
                        return;
                    }

                    if (onSuccess) {
                        onSuccess(result.data);
                        return;
                    }

                    lookupResults.innerHTML = `<div class="alert alert-success text-center fw-bold" role="alert">${result.data}</div>`;
                    lookupResults.scrollIntoView({ behavior: "smooth" });
                })
                .catch((error) => {
                    errorHandler.log_exception(
                        new Error("Error searching for class member", {
                            cause: error,
                        })
                    );
                    onError(
                        "There was an error processing your request, please try again."
                    );
                })
                .finally(() => {
                    loadingGif(loading, "hide", 250);
                });
        });
}

/**
 * Render error message
 * @param {string} message
 * @returns {void}
 */
function renderErrorMessage(message) {
    modal(
        i18n.getTranslation("error"),
        `<p>${i18n.getTranslation(message)}</p>`
    );
}

/**
 *
 * @param {string} configPath
 * @param {string} userLanguage
 * @returns {Promise<Object>}
 */
async function getConfig(configPath, userLanguage) {
    const response = await fetch(configPath);
    const config = await resolveHtmlFields(await response.json(), {
        baseUrl: response.url,
    });
    return config.claim_form[userLanguage].form_options.lookup_form;
}

function getHostname() {
    const hostname = window.location.hostname;
    if (hostname.split(".").length === 2) {
        return `www.${hostname}`;
    }
    return hostname;
}

function createApiClient() {
    const hostname = getHostname();
    const isDevEnv =
        hostname.startsWith("local") ||
        hostname === "localhost" ||
        hostname.split(".")[1] === "angeiontest";

    const apiConfig = {
        apiKey: appConfigKey,
        url: isDevEnv ? appConfigDev : appConfigProd,
    };

    return apigClientFactory.newClient(apiConfig);
}
